import type { RenewalAlert, RenewalDetails } from '@policyvision/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/auth/hooks/useAuth';
import { Badge } from '@/common/components/ui/badge';
import {
  MultiSelectDropdown,
  type MultiSelectOption,
} from '@/common/components/ui/multi-select-dropdown';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';
import { useAssignRenewals } from '@/renewal/hooks/useAssignRenewals';
import { useChangeRenewalAlerts } from '@/renewal/hooks/useChangeRenewalAlerts';

import { useChangeRenewalStatus } from '../../../hooks/useChangeRenewalStatus';
import { RenewalAlertList } from '../../../types/RenewalAlerts';
import { RenewalStatusList } from '../../../types/RenewalStatus';
import { COLORS } from '../../../utils/badges';

interface IProps {
  renewalDetails: RenewalDetails;
}

const DetailsSection = ({ renewalDetails }: IProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { changeStatus } = useChangeRenewalStatus();
  const { changeAlerts } = useChangeRenewalAlerts();
  const { assign } = useAssignRenewals();
  const labelTextClass = 'font-semibold text-sm text-ring';

  // Prepare alert options for the multiselect dropdown
  const alertOptions: MultiSelectOption<RenewalAlert>[] = RenewalAlertList.map(
    (alert) => ({
      value: alert,
      label: alert,
      variant: COLORS.ALERTS[alert],
    }),
  );

  const handleStatusChange = (newStatus: string) => {
    changeStatus(
      {
        renewalUuid: renewalDetails.uuid,
        status: newStatus,
      },
      {
        onSuccess: () => {
          toast.success(t('renewal.drawer.statusChanged'));
        },
        onError: (error: unknown) => {
          const errorData = error as {
            response?: { data?: { message?: string } };
          };
          const errorMessage =
            errorData?.response?.data?.message ||
            t('renewal.drawer.statusChangeError');
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleAlertsChange = (newAlerts: RenewalAlert[]) => {
    changeAlerts(
      {
        renewalUuid: renewalDetails.uuid,
        alerts: newAlerts,
      },
      {
        onSuccess: () => {
          toast.success(t('renewal.drawer.alertsChanged'));
        },
        onError: (error: unknown) => {
          const errorData = error as {
            response?: { data?: { message?: string } };
          };
          const errorMessage =
            errorData?.response?.data?.message ||
            t('renewal.drawer.alertsChangeError');
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleAssignToSelf = () => {
    assign(
      {
        renewalUuid: renewalDetails.uuid,
      },
      {
        onSuccess: () => {
          toast.success(t('renewal.drawer.assignedToSelf'));
        },
        onError: (error: unknown) => {
          const errorData = error as {
            response?: { data?: { message?: string } };
          };
          const errorMessage =
            errorData?.response?.data?.message ||
            t('renewal.drawer.assignationError');
          toast.error(errorMessage);
        },
      },
    );
  };

  const [assignmentState, assignmentDisplayLabel] = useMemo(() => {
    if (!renewalDetails.assignedToName) return ['unassigned', '-'];
    if (renewalDetails.assignedToName === user?.name)
      return ['self', `${user?.name} (${t('renewal.drawer.self')})`];
    return ['other', renewalDetails.assignedToName ?? '-'];
  }, [renewalDetails.assignedToName, t, user?.name]);

  return (
    <div>
      <div className="px-5 py-3 flex items-center justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.status')}
        </span>
        <Select
          value={renewalDetails.status}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RenewalStatusList.map((status) => (
              <SelectItem key={status} value={status}>
                <Badge variant={COLORS.STATUS[status]}>
                  {t(`renewal.${status}`)}
                </Badge>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="px-5 py-3 flex items-center justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.alerts')}
        </span>
        <MultiSelectDropdown
          value={renewalDetails.alerts}
          onChange={handleAlertsChange}
          options={alertOptions}
          placeholder={t('renewal.drawer.selectAlertsPlaceholder')}
          renderMenuItem={(option) => (
            <Badge variant={option.variant}>{option.label}</Badge>
          )}
        />
      </div>
      <div className="px-5 py-3 flex items-center justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.assignedTo')}
        </span>
        <span className="flex items-center gap-4">
          <Select
            value={assignmentState}
            onValueChange={(newValue) => {
              if (newValue === 'self') {
                handleAssignToSelf();
              }
            }}
          >
            <SelectTrigger>
              <SelectValue>{assignmentDisplayLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {!!renewalDetails.assignedToName &&
                renewalDetails.assignedToName !== user?.name && (
                  <SelectItem value="other" disabled>
                    {renewalDetails.assignedToName}
                  </SelectItem>
                )}
              <SelectItem value="self">
                {user?.name} ({t('renewal.drawer.self')})
              </SelectItem>
            </SelectContent>
          </Select>
        </span>
      </div>
      <hr className="w-[95%] my-4 mx-auto" />
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.contactName')}
        </span>
        <span className="text-sm">{renewalDetails.contactFullName}</span>
      </div>
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>{t('renewal.analysisTab.phone')}</span>
        <span className="text-sm">{renewalDetails.contactPhone}</span>
      </div>
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>{t('renewal.analysisTab.email')}</span>
        <span className="text-sm">{renewalDetails.contactEmail}</span>
      </div>
      <hr className="w-[95%] my-4 mx-auto" />
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.primaryBroker')}
        </span>
        <span className="text-sm">{renewalDetails.primaryBrokerName}</span>
      </div>
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.accountManager')}
        </span>
        <span className="text-sm">{renewalDetails.accountManagerName}</span>
      </div>
      <hr className="w-[95%] my-4 mx-auto" />
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.branchType')}
        </span>
        <Badge variant="outline">{renewalDetails.type}</Badge>
      </div>
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.insurer')}
        </span>
        <span className="text-sm">{renewalDetails.insurerName}</span>
      </div>
      <div className="px-5 py-2 flex justify-between">
        <span className={labelTextClass}>
          {t('renewal.analysisTab.profitCenter')}
        </span>
        <span className="text-sm">{renewalDetails.profitCenter}</span>
      </div>
    </div>
  );
};

export default DetailsSection;
